import { useGetApiV1PersonSharedPeopleMultiple } from '@/shared/api/generated';
import { type LookupOption, useDebouncedFilter } from '@/shared/ui/molecules/LookupAutocomplete';

/**
 * Mapeia o dropdown de pessoas para {@link LookupOption} **preservando a chave
 * como texto**.
 *
 * `DropDownListStringKeyViewModel.key` é um código de pessoa (`"USR260953"`), e
 * não o `personId` numérico — por isso `mapDropdownOptions` não serve aqui e a
 * chave não é convertida para número. A conversão anterior produzia `NaN` e o
 * filtro de aluno era descartado silenciosamente na montagem da query.
 */
function mapPeopleOptions(
  data: readonly { key?: string | null; value?: string | null }[] | undefined,
): readonly LookupOption[] {
  return (data ?? []).map(item => ({ id: item.key ?? '', label: item.value ?? '' }));
}

/**
 * Busca de alunos por nome (debounce de 1 s + mínimo de 3 caracteres) para o
 * autocomplete de aluno. Consome o lookup `person/shared/people-multiple`.
 *
 * O rótulo retornado tem o formato `"Nome  [email]"` — é dele que
 * `parseStudentLabel` extrai os valores enviados em `StudentName` e `Email`,
 * já que a chave textual não serve para `PersonIds`.
 *
 * @param filter - Texto digitado no campo (default `''`).
 * @returns `{ options, isLoading, isQuerySuccess }`.
 *
 * @example
 * ```tsx
 * const { options, isQuerySuccess } = useStudentSearchDropdown(search);
 * ```
 */
export function useStudentSearchDropdown(filter = '') {
  const { debouncedFilter, isReady } = useDebouncedFilter(filter);

  const { data, isLoading, isSuccess } = useGetApiV1PersonSharedPeopleMultiple(
    isReady ? { filter: debouncedFilter } : undefined,
    { query: { enabled: isReady } },
  );

  return { options: mapPeopleOptions(data), isLoading, isQuerySuccess: isSuccess } as const;
}
